/**
 * The sidebar: projects, chats, and the status of each.
 *
 * Follows the desktop app's model (which is what the user already uses daily)
 * rather than inventing one:
 *
 * - Chats group by **project**, with "No project" for chats homed on the device
 *   directly. The desktop also offers grouping by device; that is a preference
 *   the web client does not yet read, so it groups by project.
 * - Pinned chats come first under their own heading, from the synced
 *   `sidebarPins` registry row — not from local storage, so a pin set on the
 *   desktop shows up here.
 * - Each row carries a status dot, a relative time, and a preview. The dot is
 *   the desktop's indicator set: working / awaiting input / errored / completed.
 * - A row with a linked change request shows its number, the way the desktop
 *   sidebar does.
 *
 * T3's row geometry: 44px tall on touch, `rounded-lg`, a white card when
 * selected against the zinc-50 sidebar, hover a near-white. No accent wash —
 * the accent is reserved for actions.
 */

import { useMemo, useState } from "react";
import { useAppState, useStore } from "./store-context";
import { chatIndicator, chatTitle, repositoryKey, spaceLabel, toMillis, type Chat, type Space } from "../lib/models";
import { IconComet, IconSearch, IconDevice, IconChevron, IconFolder, IconPen, IconPlus } from "./icons";

const SIDEBAR_WIDTH = "clamp(260px, 26vw, 320px)";

export function Sidebar({
  hidden,
  onOpenChat,
  onNewChat
}: {
  hidden: boolean;
  onOpenChat: (chatId: string) => void;
  onNewChat: () => void;
}) {
  const state = useAppState();
  const store = useStore();
  const [query, setQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState<string | undefined>(undefined);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);

  const pinned = useMemo(() => new Set(state.pinnedChatIds), [state.pinnedChatIds]);

  /**
   * The sidebar is repository-first, like T3's project sidebar. A repository is
   * one logical project; each machine is a concrete Space underneath it. Chats
   * remain available under the machine that hosts them instead of turning every
   * machine checkout into a duplicate project heading.
   */
  const sections = useMemo(() => {
    type MachineSection = { space: Space; chats: Chat[] };
    type Section = { key: string; label: string; machines: MachineSection[]; chats: Chat[] };
    const needle = query.trim().toLowerCase();
    const byRepository = new Map<string, Section>();
    const spaceById = new Map(state.spaces.map((space) => [space.id, space]));

    for (const space of state.spaces) {
      const key = `repo:${repositoryKey(space)}`;
      const section = byRepository.get(key) ?? {
        key,
        label: spaceLabel(space),
        machines: [],
        chats: []
      };
      section.machines.push({ space, chats: [] });
      byRepository.set(key, section);
    }

    for (const chat of state.chats) {
      if (chat.archived) continue;
      const space = chat.spaceId == null ? undefined : spaceById.get(chat.spaceId);
      const key = space ? `repo:${repositoryKey(space)}` : `device:${chat.deviceId}`;
      const section = byRepository.get(key) ?? {
        key,
        label: spaceLabelFallback(chat, state.devices),
        machines: [],
        chats: []
      };
      if (space) {
        const machine = section.machines.find((entry) => entry.space.id === space.id);
        machine?.chats.push(chat);
      } else {
        section.chats.push(chat);
      }
      byRepository.set(key, section);
    }

    const sortChats = (chats: Chat[]) => chats.sort((a, b) => {
      const aPinned = pinned.has(a.id) ? 0 : 1;
      const bPinned = pinned.has(b.id) ? 0 : 1;
      if (aPinned !== bPinned) return aPinned - bPinned;
      const aAt = toMillis(a.lastMessageAt) ?? toMillis(a.createdAt) ?? 0;
      const bAt = toMillis(b.lastMessageAt) ?? toMillis(b.createdAt) ?? 0;
      return bAt !== aAt ? bAt - aAt : a.id.localeCompare(b.id);
    });
    return [...byRepository.values()]
      .map((section) => ({
        ...section,
        machines: section.machines
          .sort((a, b) => deviceLabel(a.space.deviceId, state.devices).localeCompare(deviceLabel(b.space.deviceId, state.devices)))
          .map((machine) => ({ ...machine, chats: sortChats(machine.chats) })),
        chats: sortChats(section.chats)
      }))
      .filter((section) => {
        if (projectFilter !== undefined && section.key !== projectFilter) return false;
        if (!needle) return true;
        return (
          section.label.toLowerCase().includes(needle) ||
          section.machines.some((machine) =>
            `${deviceLabel(machine.space.deviceId, state.devices)} ${machine.space.path}`.toLowerCase().includes(needle) ||
            machine.chats.some((chat) => chatTitle(chat).toLowerCase().includes(needle))
          ) ||
          section.chats.some((chat) => chatTitle(chat).toLowerCase().includes(needle))
        );
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [state.chats, state.spaces, state.devices, query, pinned, projectFilter]);

  const projectOptions = useMemo(
    () => {
      const options = new Map<string, string>();
      for (const space of state.spaces) options.set(`repo:${repositoryKey(space)}`, spaceLabel(space));
      for (const chat of state.chats) {
        if (chat.archived || chat.spaceId !== null && chat.spaceId !== undefined) continue;
        options.set(`device:${chat.deviceId}`, spaceLabelFallback(chat, state.devices));
      }
      return [...options.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    },
    [state.chats, state.devices, state.spaces]
  );

  return (
    <aside
      style={{ width: SIDEBAR_WIDTH }}
      className={`${hidden ? "hidden" : "flex"} min-h-0 shrink-0 flex-col border-r border-sidebar-border bg-sidebar md:flex`}
    >
      <div className="@container/sidebar-header relative flex h-[var(--workspace-topbar-height)] shrink-0 flex-row items-center gap-2 px-3 md:px-0">
        <button type="button" aria-label="Go to threads" onClick={onNewChat} className="relative z-10 ml-3 hidden h-7 min-w-0 shrink-0 items-center gap-1 overflow-hidden rounded-md text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex">
          <IconComet className="size-4 shrink-0 text-primary" />
          <span className="inline-flex min-w-0 items-baseline gap-1 text-sm font-medium tracking-tight">
            <span className="truncate">Zeron</span>
            <span className="truncate text-muted-foreground">Code</span>
          </span>
        </button>
      </div>
      <div className="flex items-center gap-1 px-2 py-1">
        <div className="relative flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-sm font-medium text-sidebar-muted-foreground hover:bg-sidebar-row-hover hover:text-sidebar-foreground">
          <IconSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search"
            aria-label="Search chats"
            className="h-8 w-full min-w-0 bg-transparent pr-1 pl-6 text-sm outline-none placeholder:text-sidebar-muted-foreground"
          />
        </div>
        <div className="flex shrink-0 items-center">
          {projectOptions.length > 0 ? (
            <div className="relative">
              <button
                type="button"
                aria-label="Filter threads by project"
                aria-expanded={projectMenuOpen}
                title="Filter threads by project"
                onClick={() => setProjectMenuOpen((open) => !open)}
                className="flex size-7 shrink-0 items-center justify-center rounded-md text-sidebar-muted-foreground hover:bg-sidebar-row-hover hover:text-sidebar-foreground"
              >
                <IconFolder className="size-4" />
              </button>
              {projectMenuOpen ? (
                <div className="absolute top-[calc(100%+0.35rem)] right-0 z-40 w-64 overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg">
                  <div className="px-1 pb-1">
                    <input
                      autoFocus
                      type="search"
                      placeholder="Search projects..."
                      aria-label="Search projects"
                      className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-ring"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setProjectFilter(undefined);
                      setProjectMenuOpen(false);
                    }}
                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-accent ${projectFilter === undefined ? "bg-accent" : ""}`}
                  >
                    <IconFolder className="size-4 shrink-0" />
                    All projects
                  </button>
                  {projectOptions.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setProjectFilter(key);
                        setProjectMenuOpen(false);
                      }}
                      className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-accent ${projectFilter === key ? "bg-accent" : ""}`}
                    >
                      <IconFolder className="size-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate">{label}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {projectOptions.length > 0 ? (
            <button type="button" onClick={onNewChat} aria-label="Add project" title="Add project" className="flex size-7 shrink-0 items-center justify-center rounded-md text-sidebar-muted-foreground hover:bg-sidebar-row-hover hover:text-sidebar-foreground">
              <IconPlus className="size-4" />
            </button>
          ) : null}
          <button type="button" onClick={onNewChat} aria-label="New thread" title="New thread" className="flex size-7 shrink-0 items-center justify-center rounded-md text-sidebar-muted-foreground hover:bg-sidebar-row-hover hover:text-sidebar-foreground">
            <IconPen className="size-4" />
          </button>
        </div>
      </div>

      <nav aria-label="Conversations" className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {state.chats.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            {state.link === "online" ? "No conversations yet." : "Waiting for the device…"}
          </p>
        ) : sections.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">No matches.</p>
        ) : (
          sections.map((section) => (
            <section key={section.key} className="mb-2">
              <div className="flex min-h-9 items-center gap-2 rounded-md px-2.5 py-1 text-sm font-medium text-sidebar-foreground">
                <IconFolder className="size-4 shrink-0 text-sidebar-muted-foreground" />
                <h2 className="min-w-0 flex-1 truncate">{section.label}</h2>
                <span className="text-2xs tabular-nums text-sidebar-muted-foreground">{section.machines.length}</span>
              </div>
              {section.machines.map((machine) => (
                <div key={machine.space.id} className="mb-1">
                  <div className="flex min-h-7 items-center gap-2 rounded-md px-2.5 py-1 text-xs text-sidebar-muted-foreground/80">
                    <IconDevice className="size-3.5" />
                    <span className="min-w-0 flex-1 truncate">{deviceLabel(machine.space.deviceId, state.devices)}</span>
                    <span className="max-w-28 truncate text-2xs text-sidebar-muted-foreground/60">{machine.space.path}</span>
                  </div>
                  {machine.chats.map((chat) => (
                    <ChatRow
                      key={chat.id}
                      chat={chat}
                      selected={chat.id === state.openChatId}
                      pinned={pinned.has(chat.id)}
                      indicator={store.indicatorFor(chat.id)}
                      onOpen={() => onOpenChat(chat.id)}
                    />
                  ))}
                </div>
              ))}
              {section.chats.map((chat) => (
                <ChatRow
                  key={chat.id}
                  chat={chat}
                  selected={chat.id === state.openChatId}
                  pinned={pinned.has(chat.id)}
                  indicator={store.indicatorFor(chat.id)}
                  onOpen={() => onOpenChat(chat.id)}
                />
              ))}
            </section>
          ))
        )}
      </nav>

      <SidebarFooter />
    </aside>
  );
}

function spaceLabelFallback(chat: Chat, devices: readonly { id: string; name?: string | null }[]): string {
  return devices.find((device) => device.id === chat.deviceId)?.name ?? "No project";
}

function deviceLabel(id: string, devices: readonly { id: string; name?: string | null }[]): string {
  return devices.find((device) => device.id === id)?.name ?? id;
}

function ChatRow({
  chat,
  selected,
  pinned,
  indicator,
  onOpen
}: {
  chat: Chat;
  selected: boolean;
  pinned: boolean;
  indicator: ReturnType<typeof chatIndicator>;
  onOpen: () => void;
}) {
  const state = useAppState();
  const branch = chat.branch?.trim() ?? "";
  const device = state.devices.find((candidate) => candidate.id === chat.deviceId);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-current={selected ? "true" : undefined}
      className={`group/row flex w-full flex-col gap-0.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
        selected
          ? "bg-sidebar-row-selected shadow-xs"
          : "hover:bg-sidebar-row-hover active:bg-accent"
      }`}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {pinned ? <PinMark /> : null}
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-sidebar-foreground">
          {chatTitle(chat)}
        </span>
        <span className="shrink-0 text-2xs text-sidebar-muted-foreground tabular-nums">
          {relativeTime(chat.lastMessageAt)}
        </span>
      </span>
      <span className="flex min-w-0 items-center gap-1.5">
        <IndicatorDot indicator={indicator} />
        <span className="min-w-0 flex-1 truncate text-xs text-sidebar-muted-foreground">
          {chat.lastMessagePreview ?? branch ?? ""}
        </span>
      </span>
      {branch.length > 0 && (chat.lastMessagePreview ?? "").length > 0 ? (
        <span className="truncate text-2xs text-sidebar-muted-foreground/80">{branch}</span>
      ) : null}
      {device !== undefined && state.devices.length > 1 ? (
        <span className="flex items-center gap-1 truncate text-2xs text-sidebar-muted-foreground/80">
          <IconDevice className="size-3" />
          {device.name ?? device.id}
        </span>
      ) : null}
    </button>
  );
}

/** The desktop's indicator set, as a single dot. A dot rather than a pill: it
 * has to read at a glance while scanning, and it must not compete with the
 * title for attention. */
function IndicatorDot({ indicator }: { indicator: ReturnType<typeof chatIndicator> }) {
  if (indicator === "idle") return null;
  const tone =
    indicator === "working"
      ? "bg-info"
      : indicator === "awaitingInput"
        ? "bg-warning"
        : indicator === "errored"
          ? "bg-error"
          : "bg-success";
  const label =
    indicator === "working"
      ? "Working"
      : indicator === "awaitingInput"
        ? "Needs your input"
        : indicator === "errored"
          ? "Errored"
          : "Done";
  return <span className={`size-1.5 shrink-0 rounded-full ${tone}`} title={label} aria-label={label} />;
}

function PinMark() {
  return (
    <svg viewBox="0 0 16 16" className="size-3 shrink-0 text-sidebar-muted-foreground" aria-hidden="true">
      <path
        d="M6 1.5h4l-.6 3.2 1.9 2.1v1.2H8.7v3.4L8 13l-.7-1.6V8H4.7V6.8l1.9-2.1z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Device picker and connection state, at the foot of the sidebar the way T3
 * puts its environment row. */
function SidebarFooter() {
  const state = useAppState();
  const store = useStore();
  const [open, setOpen] = useState(false);
  const current = state.devices.find((device) => device.id === state.deviceId);
  return (
    <div className="border-t border-sidebar-border p-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-sidebar-muted-foreground hover:bg-sidebar-row-hover"
      >
        <span
          className={`size-1.5 shrink-0 rounded-full ${
            state.link === "online" ? "bg-success" : state.link === "idle" ? "bg-muted-foreground" : "bg-warning"
          }`}
        />
        <span className="min-w-0 flex-1 truncate">
          {current?.name ?? current?.id ?? "No device"}
        </span>
        <IconChevron className={`size-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <div className="mt-1 flex flex-col gap-0.5">
          {state.devices.map((device) => (
            <button
              key={device.id}
              type="button"
              onClick={() => {
                setOpen(false);
                void store.selectDevice(device.id);
              }}
              className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs ${
                device.id === state.deviceId
                  ? "bg-sidebar-row-selected text-sidebar-foreground shadow-xs"
                  : "text-sidebar-muted-foreground hover:bg-sidebar-row-hover"
              }`}
            >
              <IconDevice className="size-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{device.name ?? device.id}</span>
              {device.platform !== undefined ? (
                <span className="text-2xs text-sidebar-muted-foreground/70">{device.platform}</span>
              ) : null}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void store.refreshDevices();
            }}
            className="rounded-lg px-2 py-1.5 text-left text-xs text-sidebar-muted-foreground hover:bg-sidebar-row-hover"
          >
            Refresh devices
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** "now" / "5m" / "3h" / "2d", matching the desktop sidebar's density. */
function relativeTime(iso: string | null): string {
  const at = toMillis(iso);
  if (at === undefined) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - at) / 1000));
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
